import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Heart, Wind, Flag } from "@/lib/icons";
import { WellbeingScales } from "@/components/WellbeingScales";
import { TrackerInputs } from "@/components/TrackerManager";
import { PanicSOS } from "@/components/PanicSOS";

function SymptomTags({ library, selected, onChange }) {
  const [custom, setCustom] = useState("");
  const toggle = (t) => onChange(selected.includes(t) ? selected.filter((x) => x !== t) : [...selected, t]);
  const addCustom = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const t = custom.trim().toLowerCase();
    if (t && !selected.includes(t)) onChange([...selected, t]);
    setCustom("");
  };
  return (
    <div>
      <div className="text-xs font-semibold text-stone-600 mb-1.5">Symptoms today (optional)</div>
      <div className="flex flex-wrap gap-1.5" data-testid="symptom-tags">
        {[...library, ...selected.filter((t) => !library.includes(t))].map((t) => (
          <button key={t} type="button" data-testid={`symptom-tag-${t.replace(/\s+/g, "-")}`} onClick={() => toggle(t)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition ${selected.includes(t) ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
            {t}
          </button>
        ))}
      </div>
      <div className="flex gap-1.5 mt-2">
        <input data-testid="symptom-custom-tag" value={custom} onChange={(e) => setCustom(e.target.value)} onKeyDown={addCustom}
          placeholder="Add your own + Enter" className="flex-1 px-3 py-1.5 rounded-xl border border-stone-200 text-xs focus:border-amber-600 outline-none" />
      </div>
    </div>
  );
}

export function WellbeingCheckinForm({ scaleDefs, scales, onScales, trackers, trackerVals, onTrackerVals, tagLibrary, onSubmit }) {
  const [panic, setPanic] = useState(false);
  const [panicSev, setPanicSev] = useState(0);
  const [showSOS, setShowSOS] = useState(false);
  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState([]);

  const submit = async (e) => {
    e.preventDefault();
    const ok = await onSubmit({ panic_attack: panic, panic_severity: panic ? panicSev : 0, notes, symptoms: tags });
    if (ok === false) return;
    setNotes(""); setTags([]); setPanic(false); setPanicSev(0);
  };

  return (
    <>
      <AnimatePresence>{panic && showSOS && <PanicSOS onDone={() => setShowSOS(false)} />}</AnimatePresence>
      <form onSubmit={submit} className="warm-card p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-amber-700" />
          <h3 className="font-display text-lg font-bold text-stone-900">How's today?</h3>
        </div>
        <WellbeingScales defs={scaleDefs} values={scales} onChange={(k, v) => onScales({ ...scales, [k]: v })} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" data-testid="panic-checkbox" checked={panic}
            onChange={(e) => { setPanic(e.target.checked); setShowSOS(e.target.checked); }} className="w-4 h-4 accent-amber-600" />
          <Wind className="w-4 h-4 text-stone-500" /> Panic attack today
        </label>
        {panic && (
          <div>
            <div className="flex justify-between text-xs font-semibold text-stone-600 mb-1">
              <span>Severity</span><span className="font-mono text-amber-800">{panicSev}/10</span>
            </div>
            <input type="range" min="0" max="10" data-testid="panic-severity" value={panicSev} onChange={(e) => setPanicSev(+e.target.value)} className="w-full accent-amber-600" />
          </div>
        )}
        <SymptomTags library={tagLibrary} selected={tags} onChange={setTags} />
        <TrackerInputs trackers={trackers} values={trackerVals} onChange={(id, v) => onTrackerVals({ ...trackerVals, [id]: v })} />
        <textarea data-testid="symptom-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
          placeholder="Anything else you want to note? (private, for you & your coach)"
          className="w-full px-3 py-2 rounded-xl border border-stone-300 text-sm focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none" />
        <button type="submit" data-testid="symptom-submit" className="amber-btn w-full text-sm">Log check-in</button>
      </form>
    </>
  );
}

const trackerText = (tv) => (tv.type === "yesno" ? (tv.value ? "yes" : "no") : `${tv.value}${tv.unit ? ` ${tv.unit}` : ""}`);

function CheckinRow({ s, scaleDefs }) {
  const extraScales = Object.entries(s.scales || {}).filter(([k]) => k !== "mood" && k !== "anxiety");
  const hasExtras = Object.keys(s.scales || {}).length > 2 || s.tracker_values?.length > 0;
  return (
    <li className="text-sm border-b border-stone-100 pb-2 last:border-0">
      <div className="flex items-center justify-between text-xs text-stone-500 font-mono">
        <span>{new Date(s.created_at).toLocaleDateString()}</span>
        <span>M {s.mood} · A {s.anxiety}{s.panic_attack ? ` · P${s.panic_severity}` : ""}</span>
      </div>
      {s.symptoms?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">{s.symptoms.map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">{t}</span>)}</div>
      )}
      {hasExtras && (
        <div className="flex flex-wrap gap-1 mt-1" data-testid={`checkin-extras-${s.id}`}>
          {extraScales.map(([k, v]) => <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800">{scaleDefs.find((d) => d.key === k)?.label || k} {v}</span>)}
          {(s.tracker_values || []).map((tv) => <span key={tv.tracker_id} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800">{tv.name}: {trackerText(tv)}</span>)}
        </div>
      )}
      {s.notes && <p className="text-stone-700 mt-1">{s.notes}</p>}
    </li>
  );
}

export function RecentCheckins({ symptoms, scaleDefs }) {
  return (
    <div className="warm-card p-6">
      <div className="flex items-center gap-2 mb-3">
        <Flag className="w-5 h-5 text-emerald-700" />
        <h3 className="font-display text-lg font-bold text-stone-900">Recent check-ins</h3>
      </div>
      {symptoms.length === 0 ? (
        <p className="text-sm text-stone-500">No entries yet.</p>
      ) : (
        <ul className="space-y-3 max-h-72 overflow-y-auto">{symptoms.slice(0, 10).map((s) => <CheckinRow key={s.id} s={s} scaleDefs={scaleDefs} />)}</ul>
      )}
    </div>
  );
}
